import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * Accessible modal dialog with:
 *   • role="dialog" + aria-modal="true" + aria-labelledby
 *   • Escape closes
 *   • Focus trap inside the dialog (Tab / Shift+Tab cycle)
 *   • Initial focus on first focusable element
 *   • Focus restored to the previously-focused element on close
 *   • Body scroll locked while open
 *   • Backdrop click closes (unless dismissOnBackdrop=false)
 *   • Rendered through a portal on <body> so it always stacks above the
 *     sticky page header and the mobile nav drawer, regardless of where in
 *     the tree it is mounted
 *
 * Props:
 *   open               — boolean
 *   onClose            — () => void
 *   labelledBy         — id of the element that labels the dialog (heading)
 *   children           — dialog content
 *   className          — extra classes for the dialog box
 *   dismissOnBackdrop  — default true
 *   initialFocusRef    — optional ref to focus on open
 */
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

export const Modal = ({
    open,
    onClose,
    labelledBy,
    children,
    className = '',
    dismissOnBackdrop = true,
    initialFocusRef,
}) => {
    const dialogRef = useRef(null);
    const previouslyFocusedRef = useRef(null);

    // Lock body scroll while open
    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    // Manage focus: capture previous focus, set initial focus, restore on close
    useEffect(() => {
        if (!open) return undefined;

        previouslyFocusedRef.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;

        // Defer to next frame so the dialog DOM is mounted
        const id = requestAnimationFrame(() => {
            if (initialFocusRef?.current) {
                initialFocusRef.current.focus();
                return;
            }
            const dialog = dialogRef.current;
            if (!dialog) return;
            const first = dialog.querySelector(FOCUSABLE_SELECTOR);
            if (first instanceof HTMLElement) first.focus();
            else dialog.focus();
        });

        return () => {
            cancelAnimationFrame(id);
            // Restore focus when dialog closes
            const prev = previouslyFocusedRef.current;
            if (prev && typeof prev.focus === 'function') {
                // Use rAF so the trigger isn't blocked by lingering events
                requestAnimationFrame(() => prev.focus());
            }
        };
    }, [open, initialFocusRef]);

    // Keyboard handling: Escape + focus trap
    const handleKeyDown = useCallback(
        (event) => {
            if (!open) return;
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose?.();
                return;
            }
            if (event.key !== 'Tab') return;

            const dialog = dialogRef.current;
            if (!dialog) return;
            const focusables = Array.from(
                dialog.querySelectorAll(FOCUSABLE_SELECTOR)
            ).filter((element) => !element.hasAttribute('disabled'));
            if (focusables.length === 0) {
                event.preventDefault();
                return;
            }
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        },
        [open, onClose]
    );

    if (!open) return null;
    if (typeof document === 'undefined') return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
            onClick={() => dismissOnBackdrop && onClose?.()}
            onKeyDown={handleKeyDown}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={labelledBy}
                tabIndex={-1}
                className={`bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto outline-none ${className}`}
                onClick={(event) => event.stopPropagation()}
            >
                {children}
            </div>
        </div>,
        document.body,
    );
};

